import React, {Component, Fragment} from 'react';
import {generatePath, RouteComponentProps} from 'react-router';
import {Button} from '@material-ui/core';
import RouteURLs from 'RouteURLs';
import BookingLayout from 'layouts/Booking';
import {logPageView} from 'services/analytics';
import {BookingSettings, DraftBooking, NewBooking} from 'services/entities/booking';
import {createBooking, loadBookingInitialChargeAmount, updateBooking} from 'services/bookings';
import {
  deleteDraftBooking,
  loadDraftBooking,
  removeDraftBookingDiscount,
  saveDraftBooking
} from 'services/bookings/drafts';
import BookingBanner from '../components/BookingBanner';
import {BookingStep} from '../steps';
import {isLoggedIn} from 'services/auth';
import LoginModal from 'components/auth/LoginModal';
import PaymentModal from '../components/PaymentModal';
import {handleError} from 'util/errors';
import './index.scss';
import BookingSummary from '../components/BookingSummary';
import {CurrencyAmount} from 'util/currency';
import {Discount} from 'services/entities/discounts';
import {isFlexiTicketAvailable, validateBookingDiscounts} from 'util/booking';
import Modal from 'components/Modal';
import ProgressSpinner from 'components/ProgressSpinner';
import CheckBoxField from 'components/forms/CheckBoxField';
import {loadSettings} from 'services/pricing';
import {formatPercentage} from 'util/number';
import {BookingEnquiryUser} from "services/entities/contact";
import {logBookingEnquiry} from "services/contact";
import BookingEnquiryUserModal from "components/bookings/BookingEnquiryUserModal";
import BookingEnquirySuccessModal from "components/bookings/BookingEnquirySuccessModal";
import {FormStatus} from "components/forms/Form";

interface EditBookingCheckOutPageProps extends RouteComponentProps<{code?: string}> {
}

enum BookingCheckoutModal {
  Login,
  User,
  Success,
  Payment,
  ValidatingDiscounts,
  InvalidDiscounts
}

interface EditBookingCheckOutPageState {
  booking: DraftBooking|null;
  chargedPrice: CurrencyAmount|null;
  discountErrors: {[index:string]: Error}|null;
  currentModal: BookingCheckoutModal|null;
  bookingSettings: BookingSettings|null;
  enquiryStatus: FormStatus;
}

export default class EditBookingCheckOutPage extends Component<EditBookingCheckOutPageProps, EditBookingCheckOutPageState> {
  state: EditBookingCheckOutPageState = {
    booking: null,
    chargedPrice: null,
    discountErrors: null,
    currentModal: null,
    bookingSettings: null,
    enquiryStatus: FormStatus.None
  };

  componentDidMount(): void {
    logPageView(this.props.location.pathname, 'Create Booking - Check-out');
    loadDraftBooking(this.props.match.params.code).then(
      booking => {
        if(booking) {
          this.updateBooking(booking);
        } else {
          this.props.history.replace(RouteURLs.CREATE_BOOKING_TIME);
        }
      },
      (ex) => {
        handleError('Failed to load your booking', ex);
        this.props.history.replace(RouteURLs.CREATE_BOOKING_TIME)
      }
    );
    loadSettings().then(
      settings => this.setState({bookingSettings: settings})
    )
  }

  updateBooking(booking: DraftBooking) {
    this.setState({booking: booking});
    loadBookingInitialChargeAmount(booking).then(
      price => this.setState({chargedPrice: price}),
      err => handleError('Failed to load price', err)
    );
  }

  saveEnquiry(user?: BookingEnquiryUser) {
    const booking = this.state.booking;
    if(!booking) {
      console.warn("Booking not loaded");
      return;
    }
    this.setState({enquiryStatus: FormStatus.Submitting});
    logBookingEnquiry({
      space: booking.space,
      times: booking.times.map(t => { return {start: t.startTime, end: t.endTime}; }),
      user: user || null,
    }).then(
      () => this.setState({enquiryStatus: FormStatus.Success, currentModal: BookingCheckoutModal.Success}),
      ex => {
        handleError("Failed saving enquiry", ex);
        this.setState({enquiryStatus: FormStatus.Failure})
      }
    );
  }

  onCompleteBookingClick = () => {
    const booking = this.state.booking;
    if(!booking) {
      console.warn("Booking not loaded");
      return;
    }
    if(isLoggedIn()) {
      if (booking!.code) {
        updateBooking(booking!.code, booking!.products).then(
          booking => {
            deleteDraftBooking();
            this.props.history.push(generatePath(RouteURLs.CREATE_BOOKING_SUCCESS, { code: booking.code }) + "?updated");
          },
          err => {
            handleError('Failed to update booking', err);
          }
        );
      } else {
        this.setState({currentModal: BookingCheckoutModal.Payment});
      }
    } else {
      this.setState({currentModal: BookingCheckoutModal.Login});
    }
  };

  onOpenEnquiryFormClick = () => {
    if(isLoggedIn()) {
      this.saveEnquiry();
    } else {
      this.setState({currentModal: BookingCheckoutModal.User});
    }
  };

  onUserModalSaved = (user: BookingEnquiryUser) => {
    this.setState({currentModal: null});
    this.saveEnquiry(user);
  };

  onUserModalCancelled = () => {
    this.setState({currentModal: null});
  };

  onSuccessModalClosing = () => {
    this.setState({currentModal: null});
  };

  onLoggedIn = () => {
    const booking: DraftBooking|null = this.state.booking;
    if(booking && booking!.discounts.length > 0) {
      this.setState({currentModal: BookingCheckoutModal.ValidatingDiscounts});
      // need to revalidate discounts - user might have exceeded usage count
      validateBookingDiscounts(booking!.discounts).then(
        discountErrors => {
          const invalidCodes = Object.keys(discountErrors);
          if(invalidCodes.length > 0) {
            // booking has invalid discounts - remove them and display to user
            for(const code of invalidCodes) {
              removeDraftBookingDiscount(booking, code);
            }
            saveDraftBooking(booking);
            this.updateBooking(booking);
            this.setState({currentModal: BookingCheckoutModal.InvalidDiscounts, discountErrors: discountErrors});
          } else {
            this.setState({currentModal: BookingCheckoutModal.Payment});
          }
        },
        ex => {
          handleError('Failed to validate discounts', ex);
          this.setState({currentModal: BookingCheckoutModal.Payment});
        }
      );
    } else {
      this.setState({currentModal: BookingCheckoutModal.Payment});
    }
  };

  onLoginCancelled = () => {
    this.setState({currentModal: null});
  };

  onDiscountsChanged = () => {
    const booking = this.state.booking;
    if(!booking) {
      return;
    }
    this.updateBooking(booking);
  };

  onDiscountRemoved = (discount: Discount) => {
    const booking = this.state.booking;
    if(!booking) {
      return;
    }
    removeDraftBookingDiscount(booking, discount.code);
    saveDraftBooking(booking);
    this.updateBooking(booking);
  };

  onCloseInvalidDiscountsModal = () => {
    this.setState({currentModal: null});
  };

  onAcceptInvalidDiscountsModalClick = () => {
    this.setState({currentModal: BookingCheckoutModal.Payment});
  };

  onPaymentCancelled = () => {
    this.setState({currentModal: null});
  };

  onPaymentSuccess = async (paymentMethodID: string) => {
    const draftBooking: DraftBooking|null = this.state.booking;
    if(!draftBooking) {
      console.warn("Booking not set");
      return;
    }
    const newBooking: NewBooking = {
      space: draftBooking!.space!,
      design: null,
      times: draftBooking!.times.concat(),
      products: draftBooking!.products,
      discounts: draftBooking!.discounts
    };
    let bookings;
    try {
      bookings = await createBooking(newBooking, paymentMethodID);
    } catch(ex) {
      handleError('Create booking failed', ex);
      throw ex;
    }
    deleteDraftBooking();
    this.props.history.push(generatePath(RouteURLs.CREATE_BOOKING_SUCCESS, { code: bookings[0].code }) + "?new");
  };

  onPurchaseFlexiTicketChanged = (index: number, checked: boolean) => {
    const booking: DraftBooking|null = this.state.booking;
    if(!booking) {
      return;
    }
    booking!.times[index].includeFlexiTicket = checked;
    saveDraftBooking(booking);
    this.updateBooking(booking);
  };

  render() {
    const {booking, chargedPrice, currentModal, discountErrors, enquiryStatus} = this.state;
    return <BookingLayout id="create-booking-checkout-page">
      <BookingBanner title='Check out' subtitle='Review your booking' currentStep={BookingStep.CheckOut} booking={booking}/>
      { booking && <Fragment>
        { this.renderFlexiTicketSection(booking) }
        <h2>My booking summary</h2>
        <BookingSummary booking={booking} onDiscountRemoved={this.onDiscountRemoved}
                        onDiscountsChanged={this.onDiscountsChanged} priceChildren={
          <Fragment>
            <Button onClick={this.onOpenEnquiryFormClick} disabled={enquiryStatus === FormStatus.Submitting || enquiryStatus === FormStatus.Success}>
              { enquiryStatus === FormStatus.Submitting && <ProgressSpinner /> }
              Enquire Now
            </Button>
          </Fragment>}/>
      </Fragment> }
      <Modal open={currentModal === BookingCheckoutModal.ValidatingDiscounts}
             closable={false} className="validating-discounts-modal">
        <h2>Validating discounts</h2>
        <ProgressSpinner />
      </Modal>
      <Modal open={currentModal === BookingCheckoutModal.InvalidDiscounts}
             className="invalid-discounts-modal" onClosing={this.onCloseInvalidDiscountsModal}>
        <h2>Discount error</h2>
        { discountErrors && Object.keys(discountErrors).map((code, index) => <p key={index}>
          The discount code {code} is invalid. The booking price has been updated.</p>) }
        <Button className="continue" onClick={this.onAcceptInvalidDiscountsModalClick}>Continue</Button>
      </Modal>
      <BookingEnquiryUserModal open={currentModal === BookingCheckoutModal.User} onSaved={this.onUserModalSaved}
                           onCancelled={this.onUserModalCancelled} />
      <BookingEnquirySuccessModal open={currentModal === BookingCheckoutModal.Success} onClosing={this.onSuccessModalClosing} />
      <LoginModal open={currentModal === BookingCheckoutModal.Login}
                  onLoggedIn={this.onLoggedIn} onCancelled={this.onLoginCancelled}/>
      { booking && chargedPrice && <PaymentModal open={currentModal === BookingCheckoutModal.Payment} price={chargedPrice} booking={booking}
                    onSuccess={this.onPaymentSuccess} onCancelled={this.onPaymentCancelled} /> }
    </BookingLayout>;
  }

  renderFlexiTicketSection(booking: DraftBooking) {
    const settings: BookingSettings|null = this.state.bookingSettings;
    if(!settings || !booking.times.some(t => isFlexiTicketAvailable(t.startTime, settings))) {
      return null;
    }
    return <div className="flexi-ticket">
      <h2>Flex-Ticket <span>+ { formatPercentage(settings!.flexiTicketRate)} of Rental Cost</span></h2>
      <p>Flexi-Ticket enables you to move your booking to within 12 months of the original booking (any
        price difference in selected time slots will apply). Bookings with a “flexi-ticket” can made up to
        14 days before the current scheduled booking. You will not be able to purchase after booking.</p>
      <h3>Purchase Flexi-Ticket</h3>
      { booking.times.map((t, i) => {
        if(!isFlexiTicketAvailable(t.startTime, settings)) {
          return null;
        } else {
          const label = t.startTime.format('dddd D MMMM h:mmA') + ' - ' + t.endTime!.format("h:mmA");
          return <CheckBoxField key={i} label={label}
                                onChange={(checked) => this.onPurchaseFlexiTicketChanged(i, checked)}
                                checked={t.includeFlexiTicket}/>;
        }
      })}
    </div>;
  }
}